<template>
  <WdsAlert v-if="validationErrors && validationErrors.length" error class="tw-mb-2">
    <ul class="tw-mb-0">
      <li v-for="validationError in validationErrors" :key="validationError">
        {{ validationError }}
      </li>
    </ul>
  </WdsAlert>
  <div class="tw-h-[60vh] tw-overflow-y-auto">
    <RecruitersTable
      multi-select
      :pre-selected-recruiters="preSelectedRecruiters"
      @rows-selected="handleRowsSelected"
    />
  </div>
  <div
    class="-tw-mx-6 select-talent-footer tw-flex tw-items-center tw-justify-between tw-pt-4 tw-pb-5 tw-px-6"
  >
    <WdsButton @click="$emit('cancel')">
      {{ $t('Cancel') }}
    </WdsButton>

    <WdsStack direction-row align-center gap-md>
      <WdsSpinner v-if="loading" indeterminate />

      <WdsStack direction-row align-center gap-sm>
        <WdsAvatarGroup sm :max-avatars="3" :avatars="selectedRecruitersAvatars" />

        <WdsButton
          :disabled="!hasChangedShares"
          primary
          data-test="update-collaborators"
          @click="handleConfirm"
        >
          {{
            preSelectedRecruiters.length > 0
              ? $t('Update staffing agencies')
              : $t('Add staffing agencies')
          }}
        </WdsButton>
      </WdsStack>
    </WdsStack>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, Ref } from 'vue'
import { WdsButton, WdsAvatarGroup, WdsSpinner, WdsAlert, WdsStack } from '@worksome/wds-components'
import { useMutation } from '@vue/apollo-composable'
import NetworkError from '@/utils/notification/NetworkError'
import { $t } from '@/composables/translation'
import useApolloError from '@/composables/useApolloError'
import RecruitersTable from '@/components/Contacts/CompanyRecruiter/RecruitersTable.vue'
import { formatRecruiterAsAvatar } from '@/components/Job/functions'
import { Recruiter } from '@/components/Job/hiring-types'
import {
  CreateJobShareMutation,
  RemoveJobShareMutation,
} from '@/components/Job/Share/graphql/mutations'
import { JobFragment } from '@/components/Job/graphql/fragments'
import { deepClone } from '@/utils/tools'
import { FragmentType, useFragment } from '@/gql'

const props = withDefaults(
  defineProps<{
    jobId: string
    preSelectedRecruiters: Recruiter[]
  }>(),
  {
    preSelectedRecruiters: () => [],
  },
)

const emit = defineEmits(['updated', 'cancel'])

const updateJobInCache = (cache, isForRecruiters, recruiterSharesCount) => {
  const job = deepClone(
    cache.readFragment({
      id: `Job:${props.jobId}`,
      fragment: JobFragment,
    }),
  )

  if (job) {
    job.isForRecruiters = isForRecruiters
    job.recruiterShares.paginatorInfo.total = recruiterSharesCount

    cache.writeFragment({
      id: `Job:${props.jobId}`,
      fragment: JobFragment,
      data: {
        ...job,
      },
    })
  }
}

const {
  loading: createJobShareLoading,
  mutate: mutateCreateJobShare,
  error: errorCreateJobShare,
} = useMutation(CreateJobShareMutation, {
  clientId: 'publicApi',
  update(cache, jobShare) {
    const job = jobShare.data?.createJobShare[0].job as FragmentType<typeof JobFragment>
    const jobObj = useFragment(JobFragment, job)

    const isForRecruiters = jobObj?.isForRecruiters
    const recruiterSharesCount = jobObj?.recruiterShares.paginatorInfo.total

    updateJobInCache(cache, isForRecruiters, recruiterSharesCount)
  },
})

const {
  loading: removeJobShareLoading,
  mutate: mutateRemoveJobShare,
  error: errorRemoveJobShare,
} = useMutation(RemoveJobShareMutation, {
  clientId: 'publicApi',
  update(cache, jobShare) {
    const job = jobShare.data?.removeJobShare[0].job as FragmentType<typeof JobFragment>
    const jobObj = useFragment(JobFragment, job)

    const isForRecruiters = jobObj?.isForRecruiters
    const recruiterSharesCount = jobObj?.recruiterShares.paginatorInfo.total

    updateJobInCache(cache, isForRecruiters, recruiterSharesCount)
  },
})

const preSelectedRecruiters = computed(() => props.preSelectedRecruiters ?? [])

const selectedRecruiters = ref<Recruiter[]>(preSelectedRecruiters.value)

const loading = computed(() => createJobShareLoading.value || removeJobShareLoading.value)

const selectedRecruitersAvatars = computed(() =>
  selectedRecruiters.value.map(formatRecruiterAsAvatar),
)

const handleRowsSelected = (rows: Recruiter[]) => {
  selectedRecruiters.value = Object.values(rows)
}

const recruitersToBeShared = computed(() =>
  selectedRecruiters.value.filter(
    (contact: Recruiter) =>
      !preSelectedRecruiters.value.find(
        (recruiter: Recruiter) => recruiter.recruiterId === contact.recruiterId,
      ),
  ),
)

const recruitersToBeUnshared = computed(() =>
  preSelectedRecruiters.value.filter(
    (recruiter: Recruiter) =>
      !selectedRecruiters.value.find(
        (contact: Recruiter) => contact.recruiterId === recruiter.recruiterId,
      ),
  ),
)

const hasChangedShares = computed(
  () => recruitersToBeShared.value.length > 0 || recruitersToBeUnshared.value.length > 0,
)

const { validationErrors: validationErrorsCreateJobShare }: { validationErrors: Ref<string[]> } =
  useApolloError(errorCreateJobShare)
const { validationErrors: validationErrorsRemoveJobShare }: { validationErrors: Ref<string[]> } =
  useApolloError(errorRemoveJobShare)

const validationErrors = computed(() => [
  ...Object.values(validationErrorsCreateJobShare.value),
  ...Object.values(validationErrorsRemoveJobShare.value),
])

const handleConfirm = async () => {
  try {
    if (recruitersToBeShared.value.length > 0) {
      await mutateCreateJobShare({
        input: {
          job: props.jobId,
          accounts: recruitersToBeShared.value.map((contact) => contact.recruiterId),
        },
      })
    }

    if (recruitersToBeUnshared.value.length > 0) {
      await mutateRemoveJobShare({
        input: {
          ids: recruitersToBeUnshared.value.map((contact) => contact.id),
        },
      })
    }

    if (hasChangedShares.value) {
      emit('updated')
    }

    emit('cancel')
  } catch (ex: any) {
    new NetworkError({
      ex,
      msg: $t('An error occurred'),
    }).dispatch()
  }
}
</script>

<style lang="scss" scoped>
@import 'resources/sass/frontend/_color-variables';

.select-talent-footer {
  border-top: 1px solid $gray-300;
}
</style>
